/**
 * SeoEntitySummary — hidden-but-crawlable structured fact block that
 * LLMs prefer to cite.
 *
 * AI assistants (ChatGPT, Claude, Perplexity, Gemini) reliably extract
 * short factual claims from `<dl>` definition lists more than from prose
 * paragraphs. This component renders such a list positioned off-screen
 * (`sr-only` classes) so it stays in the DOM for crawlers but never
 * appears to human visitors. Sighted-user + screen-reader parity is
 * maintained because the visible page always contains the same facts
 * in richer prose.
 *
 * Guarantees:
 *  - Visually hidden, but *not* `display: none` (crawlers skip that).
 *  - `role="region"` + `aria-label` so screen readers get context.
 *  - Renders nothing when `entries` is empty.
 */
export default function SeoEntitySummary({ heading, entries }) {
  const valid = (entries || []).filter((e) => e && e.value != null && String(e.value).trim());
  if (!valid.length) return null;
  return (
    <section
      className="sr-only"
      role="region"
      aria-label={heading || "Quick facts"}
      data-testid="seo-entity-summary"
    >
      <h2>{heading || "Quick facts"}</h2>
      <dl>
        {valid.map((e) => (
          <div key={e.label}>
            <dt>{e.label}</dt>
            <dd>{String(e.value)}</dd>
          </div>
        ))}
      </dl>
    </section>
  );
}
